import { ColorPicker } from './ColorPicker';
import { GradientEditor } from './GradientEditor';
import { IGridLayoutParameters } from './GridLayout';
import { Button } from './SimpleButton';

import { SelectImage } from './SelectImage';
import { BaseCompositeControl } from './BaseCompositeControl';
import { LayoutBuilder } from './LayoutBuilder';
import * as commonUtils from '@Common/Helpers/CommonUtils';
import instanceFactory, { Background, ImageBackground, LinearGradientBackground, RadialGradientBackground, SolidBackground } from '@InstanceFactory';
import { enumerable } from '@Common/Core/CoreDecorator';

export enum BackgroundEditorTypes {
    solidBackground = 'SolidBackground',
    linearGradientBackground = 'LinearGradientBackground',
    radialGradientBackground = 'RadialGradientBackground',
    imageBackground = 'ImageBackground'
}

export type INamedColorsDefinition = boolean | IMap<string>;
export type BackgroundEditorSetting = {
    types?: BackgroundEditorTypes[];
    namedColors?: INamedColorsDefinition;
    showButton?: boolean;
};
type AllBackground = SolidBackground & LinearGradientBackground & RadialGradientBackground & ImageBackground;

export class BackgroundEditor extends BaseCompositeControl {
    public readonly onOkClick = this.eventBags.create<IHandler>();
    public readonly onCancelClick = this.eventBags.create<IHandler>();
    public readonly onChange = this.eventBags.create<IHandler>();
    private readonly backgroundsObjects: _.Dictionary<Background>;
    private readonly typeChooser = $(`<div class='color-button-container'/>`);
    private readonly colorPicker: ReturnType<typeof ColorPicker>;
    private readonly gradient = new GradientEditor({});
    private readonly image = new SelectImage();
    private readonly btnCancel = new Button({ text: strings.cancel, css: { marginLeft: '10px' } });
    private readonly btnOk = new Button({ text: strings.ok, css: { marginLeft: 'auto' }, className: 'accept' });
    private readonly chooseButtons: Button[] = [];

    private currentTypeName = null;

    @enumerable
    private get currentBackground(): AllBackground {
        return this.backgroundsObjects[this.currentTypeName] as any;
    }

    constructor(param: BackgroundEditorSetting, private readonly showButton: boolean = param.showButton || false) {
        super();

        const types = param.types || (Object.values(BackgroundEditorTypes) as BackgroundEditorTypes[]);
        param = { ...param, types: [null, ...types] };

        this.backgroundsObjects = param.types.filter(_.identity).reduce((p, c) => {
            const bgType = instanceFactory[c];
            p[bgType.typeName] = bgType.create();
            return p;
        }, {} as _.Dictionary<Background>);

        for (const type of param.types) {
            const icon = backgroundTypeIcon(type);
            const btn = new Button({
                icon: icon,
                width: 16,
                height: 16,
                backgroundColor: 'transparent',
                className: 'narrow'
            });

            const typeName = type && instanceFactory[type].typeName;
            btn.tag(typeName);
            btn.value(type === null);
            btn.onClick(() => this.typeChange(typeName));
            btn.getControl().appendTo(this.typeChooser);
            this.chooseButtons.push(btn);
        }

        this.colorPicker = ColorPicker({ namedColors: param.namedColors });
        this.colorPicker.onChange(() => {
            this.currentBackground.color = this.colorPicker.getNamedColor();
            this.onChange();
        });

        this.gradient.onChanged(this.onChange.toFunction());
        this.btnCancel.onClick(this.onCancelClick.toFunction());
        this.btnOk.onClick(this.onOkClick.toFunction());

        this.createRoot();
    }

    private typeChange(typeName: string | null): void {
        this.currentTypeName = typeName;
        this.root.getCell(1, 0).cell.removeClass('gradient-picker');
        this.toggleChooseButtons();
        switch (typeName) {
            case ImageBackground.typeName:
                this.image.setValue(this.currentBackground);
                this.root.cellContent(1, 0, this.image);
                break;
            case instanceFactory.LinearGradientBackground.typeName:
            case instanceFactory.RadialGradientBackground.typeName:
                this.gradient.value(this.currentBackground);
                this.root.cellContent(1, 0, this.gradient);
                this.root.getCell(1, 0).cell.addClass('gradient-picker');
                break;
            case instanceFactory.SolidBackground.typeName:
                this.colorPicker.setNamedColor(this.currentBackground.color);
                this.root.cellContent(1, 0, this.colorPicker);
                break;
            default:
                this.root.cellContent(1, 0, ' ');
        }
        this.root.adjust();
        this.onChange();
    }

    private toggleChooseButtons(): void {
        for (const chooseButton of this.chooseButtons) {
            chooseButton.value(chooseButton.tag() === this.currentTypeName);
        }
    }

    protected initLayout(): IGridLayoutParameters {
        const builder = new LayoutBuilder();
        builder.addOneControl(this.typeChooser);
        builder.addOneControl(' ', true);
        if (this.showButton) {
            const buttonContainer = $(`<div style="min-width: 210px; display: flex" />`);
            for (const button of [this.btnOk, this.btnCancel]) {
                button.getControl().appendTo(buttonContainer);
            }

            builder.addOneControl(buttonContainer);
        }
        return builder.getLayout();
    }

    public setBackground(background: Background): void {
        if (background != null && this.backgroundsObjects.hasOwnProperty(background.$type)) {
            this.backgroundsObjects[background.$type] = commonUtils.clone(background);
            this.typeChange(background.$type);
        } else {
            this.typeChange(null);
        }
    }

    public getBackground(): Background {
        return this.currentBackground;
    }
}

function backgroundTypeIcon(type: BackgroundEditorTypes | null): string {
    switch (type) {
        case BackgroundEditorTypes.imageBackground:
            return 'BackgroundEditor/ImageBrush.png';
        case BackgroundEditorTypes.linearGradientBackground:
            return 'BackgroundEditor/HorizontalGradient.png';
        case BackgroundEditorTypes.radialGradientBackground:
            return 'BackgroundEditor/RadialGradient.png';
        case BackgroundEditorTypes.solidBackground:
            return 'BackgroundEditor/SolidColorBrush.png';
        default:
            return 'BackgroundEditor/NullBackground.png';
    }
}
